import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles, siteInfo } from '@/mocks/articles';
import { categories } from '@/mocks/videos';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? articles[slug as keyof typeof articles] : undefined;

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="pt-24 pb-16 text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-950">文章未找到</h1>
          <Link to="/" className="mt-4 inline-block text-primary-600 hover:text-primary-700 cursor-pointer">返回首页</Link>
        </div>
        <Footer />
      </>
    );
  }

  const category = categories.find((c) => c.slug === article.categorySlug);
  const relatedArticles = article.relatedSlugs
    .map((s) => articles[s as keyof typeof articles])
    .filter(Boolean);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: article.title,
        description: article.excerpt,
        image: article.thumbnail,
        datePublished: article.date,
        author: {
          '@type': 'Organization',
          name: article.author,
        },
        publisher: {
          '@type': 'Organization',
          name: siteInfo.name,
        },
        url: `${siteInfo.url}/article/${slug}`,
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `${siteInfo.url}/article/${slug}`,
        },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: '首页',
            item: siteInfo.url,
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: article.category,
            item: `${siteInfo.url}/category/${article.categorySlug}`,
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: article.title,
          },
        ],
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />

      <Navbar />

      <main className="pt-20">
        <article>
          <div className="bg-background-100 border-b border-background-200/70">
            <div className="px-4 md:px-6 py-8 md:py-10">
              <div className="max-w-4xl mx-auto">
                <nav className="flex items-center flex-wrap gap-2 text-xs text-foreground-500 mb-6">
                  <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
                  <span className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className="ri-arrow-right-s-line"></i>
                  </span>
                  {category && (
                    <>
                      <Link to={`/category/${article.categorySlug}`} className="hover:text-primary-600 transition-colors cursor-pointer">
                        {article.category}
                      </Link>
                      <span className="w-3.5 h-3.5 flex items-center justify-center">
                        <i className="ri-arrow-right-s-line"></i>
                      </span>
                    </>
                  )}
                  <span className="text-foreground-800 font-medium truncate max-w-[200px]">{article.title}</span>
                </nav>

                <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 text-xs rounded-full font-medium mb-4">
                  {article.category}
                </span>

                <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 leading-tight">
                  {article.title}
                </h1>

                <div className="flex flex-wrap items-center gap-4 mt-5 text-sm text-foreground-500">
                  <span className="flex items-center gap-1.5">
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-user-line"></i>
                    </span>
                    {article.author}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-calendar-line"></i>
                    </span>
                    {article.date}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-eye-line"></i>
                    </span>
                    {article.views} 次观看
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-time-line"></i>
                    </span>
                    {article.duration}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="px-4 md:px-6 py-8 md:py-12">
            <div className="max-w-4xl mx-auto">
              <div className="rounded-lg overflow-hidden mb-10">
                <img
                  src={article.thumbnail}
                  alt={article.title}
                  className="w-full aspect-video object-cover"
                />
              </div>

              <div
                className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed
                  [&_h2]:text-xl [&_h2]:md:text-2xl [&_h2]:font-heading [&_h2]:font-bold [&_h2]:text-foreground-950 [&_h2]:mt-10 [&_h2]:mb-4
                  [&_p]:mb-5 [&_p]:leading-relaxed
                  [&_strong]:text-foreground-950"
                dangerouslySetInnerHTML={{ __html: article.content }}
              >
              </div>

              <div className="flex flex-wrap items-center gap-2 mt-10 pt-8 border-t border-background-200/70">
                <span className="text-xs text-foreground-500 mr-2">标签：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full font-medium"
                  >
                    #{tag}
                  </span>
                ))}
              </div>

              <div className="flex items-center gap-4 mt-8 pt-8 border-t border-background-200/70">
                <button className="flex items-center gap-2 px-5 py-2.5 bg-background-100 hover:bg-primary-50 text-foreground-700 hover:text-primary-600 text-sm font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-thumb-up-line"></i>
                  </span>
                  点赞
                </button>
                <button className="flex items-center gap-2 px-5 py-2.5 bg-background-100 hover:bg-primary-50 text-foreground-700 hover:text-primary-600 text-sm font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-share-forward-line"></i>
                  </span>
                  分享
                </button>
                <button className="flex items-center gap-2 px-5 py-2.5 bg-background-100 hover:bg-primary-50 text-foreground-700 hover:text-primary-600 text-sm font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-bookmark-line"></i>
                  </span>
                  收藏
                </button>
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="bg-background-100 py-12 md:py-16">
            <div className="px-4 md:px-6">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-8">
                  相关推荐
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {relatedArticles.map((rel) => (
                    <Link
                      key={rel.slug}
                      to={`/article/${rel.slug}`}
                      className="group bg-background-50 rounded-lg overflow-hidden transition-all duration-300 hover:scale-[1.02] cursor-pointer"
                    >
                      <div className="aspect-video overflow-hidden">
                        <img
                          src={rel.thumbnail}
                          alt={rel.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-4">
                        <h3 className="text-sm font-heading font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">
                          {rel.title}
                        </h3>
                        <div className="flex items-center gap-3 mt-2 text-xs text-foreground-400">
                          <span>{rel.views}</span>
                          <span>{rel.date}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}